import React, { useMemo } from 'react';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';
import { Grid, Paper, Typography } from '@mui/material';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import { useDispatch, useSelector } from 'react-redux';
import { addCarrierSettings, updateCarrierSettings } from '@Reducers/User/actions';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import * as Yup from 'yup';
import { nandanCarrier } from './Type';
import { setCarrierSettings } from '@Reducers/User';
import {
  PickupPincodeOptions,
  ProductIdsOptions,
} from '../../ViewSingleShipmentAdmin/components/ShipmentProcessingV2/components/Nandan/constants';

interface propsTypes {
  onClose: () => void;
  isEditable: boolean;
}

// ... validation schema from above ...
const nandanValidationSchema = Yup.object().shape({
  accountNo: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Account value is required'),
        display: Yup.string().required('Account display is required'),
      }),
    )
    .min(1, 'At least one account number is required')
    .required('Account number is required')
    .default([]),

  product_id: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Product ID value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Product ID is required')
    .required('Product ID is required')
    .default([]),

  pickup_pincode: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string().required('Pickup pincode value is required'),
        display: Yup.string(),
      }),
    )
    .min(1, 'At least one Pickup pincode is required')
    .required('Pickup pincode is required')
    .default([]),
});

const Nandan = (props: propsTypes) => {
  const { onClose, isEditable } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { vendersAccount } = useSelector((state: RootStateType) => state.adminShipment);
  const { userDataById, carrierSetting } = useSelector((state: RootStateType) => state.user);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })),
    [vendersAccount],
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<nandanCarrier>({
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: nandanValidationSchema,
    initialValues: {
      ...carrierSetting.shree_nandan_courier_limited,
    },
    onSubmit(values) {
      if (userDataById?._id) {
        if (carrierSetting.shree_nandan_courier_limited._id) {
          dispatch(updateCarrierSettings({ ...carrierSetting.shree_nandan_courier_limited, ...values }));
        } else {
          dispatch(addCarrierSettings({ ...carrierSetting.shree_nandan_courier_limited, ...values }));
        }
      } else {
        dispatch(setCarrierSettings({ values, carrierType: 'shree_nandan_courier_limited' }));
      }
      onClose();
    },
  });

  return (
    <div>
      <>
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Nandan Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label={`Nandan Account`}
                    required
                    multiple={true}
                    placeholder="Select"
                    name="accountNo"
                    value={values?.accountNo?.map(item => item.value)}
                    options={accountOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={Array.isArray(errors.accountNo) ? errors.accountNo.join(', ') : errors.accountNo}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="product_id"
                    label={`Service`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="product_id"
                    value={values?.product_id?.map(item => item.value)}
                    options={ProductIdsOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('product_id', selectedObj);
                    }}
                    error={errors.product_id as string}
                  />
                </Grid>
              </Grid>

              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="pickup_pincode"
                    label={`Pickup Pincode`}
                    required
                    multiple={true}
                    placeholder="Select "
                    name="pickup_pincode"
                    value={values?.pickup_pincode?.map(item => item.value)}
                    options={PickupPincodeOptions}
                    onChange={(_, selectedObj) => {
                      setFieldValue('pickup_pincode', selectedObj);
                    }}
                    error={errors.pickup_pincode as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      type="submit"
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: `4px !important` }}
                      className="w-full"
                      onClick={() => {
                        handleSubmit();
                      }}
                    >
                      Save
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        type="button"
                        sx={{ borderRadius: `4px !important` }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </>
    </div>
  );
};

export default Nandan;
